/**
 * Interactive Artifact Data Contracts (SCH-3664, U1)
 *
 * An `interactive` artifact keeps one set of live data shared by all its viewers (R7). Each data item is one stored
 * row beside the page in the ops store (KTD4), addressed by a piece key and an item key. The page declares its keys
 * in markup (`data-sj-piece`, `data-sj-key`, `data-sj-item`, plus an optional `<script type="application/sj-data">`
 * block for custom keys); the store extracts that manifest at publish, update and check and records it on the
 * artifact (KTD5). Reads return only declared, type-matching items; writes to undeclared keys refuse.
 *
 * Every item records who changed it and when (R9). Team members see names; viewers outside the team get the
 * outsider item, which is strict and so can never carry a name (KD10).
 *
 * The limits below are exported for the store to enforce server-side. The item cap (500) is a count over every
 * stored row, so no schema here can enforce it.
 *
 * The write operations' parameter schemas are shared with the bridge protocol (artifact-bridge.ts), so the agent
 * route and the page's port accept exactly the same writes.
 *
 * Plan: docs/plans/2026-10-19-1623-feat-ai-artifacts-live-data-plan.md (U1).
 */

import { z } from 'zod'

// =============================================================================
// Limits (KTD4)
// =============================================================================

export const ARTIFACT_DATA_KEY_MAX_LENGTH = 64
export const ARTIFACT_DATA_TEXT_MAX_LENGTH = 4_000
export const ARTIFACT_DATA_LIST_ITEM_MAX_LENGTH = 1_000
/** At most this many stored rows per artifact, hidden ones included. Enforced by the store's counter document. */
export const ARTIFACT_DATA_MAX_ITEMS = 500
/** At most this many declarations in one page's manifest. */
export const ARTIFACT_DATA_MAX_KEYS = 200
/** At most this many operations in one `data set` batch. */
export const ARTIFACT_DATA_SET_MAX_OPS = 50

// =============================================================================
// Primitives
// =============================================================================

/**
 * A piece or item key: 1–64 characters of lower-case letters, digits, `.`, `_` and `-`. Upper case is refused
 * rather than folded, so two spellings never name one item. In JavaScript `$` without the `m` flag matches only
 * at the end of input, so a trailing newline fails (pinned in the tests).
 */
export const ArtifactDataKeySchema = z
  .string()
  .min(1)
  .max(ARTIFACT_DATA_KEY_MAX_LENGTH)
  .regex(/^[a-z0-9._-]+$/, 'lower-case letters, digits, ".", "_" and "-"')
export type ArtifactDataKey = z.infer<typeof ArtifactDataKeySchema>

/** The ready-made pieces (R8), plus `custom` for keys a page reads and writes directly. */
export const ArtifactDataPieceTypeSchema = z.enum(['checklist', 'text', 'list', 'custom'])
export type ArtifactDataPieceType = z.infer<typeof ArtifactDataPieceTypeSchema>

/** The value type a custom key declares in the page's `application/sj-data` block (`{"keys":{"go-live":"text"}}`). */
export const ArtifactDataCustomValueTypeSchema = z.enum(['text', 'number', 'boolean'])
export type ArtifactDataCustomValueType = z.infer<typeof ArtifactDataCustomValueTypeSchema>

/** A stored value is a scalar (KTD4). Strings are capped at the text limit; list items are capped tighter. */
export const ArtifactDataScalarSchema = z.union([
  z.string().max(ARTIFACT_DATA_TEXT_MAX_LENGTH),
  z.number(),
  z.boolean(),
  z.null()
])
export type ArtifactDataScalar = z.infer<typeof ArtifactDataScalarSchema>

const TextSchema = z.string().max(ARTIFACT_DATA_TEXT_MAX_LENGTH)
const ListTextSchema = z.string().max(ARTIFACT_DATA_LIST_ITEM_MAX_LENGTH)
/** A list item's fractional order key. Server-computed; callers name neighbours instead. */
const OrderKeySchema = z.string().min(1).max(128)
const EpochMsSchema = z.number().int().nonnegative()
/** A team member's display name, resolved by the store from its `human:<member name>` actor. */
const ActorNameSchema = z.string().min(1).max(200)

/**
 * Names one data key: a piece, plus the item for a checklist entry or a list entry. A text field and a custom key
 * are a piece with no item.
 */
export const ArtifactDataKeyRefSchema = z
  .object({ piece: ArtifactDataKeySchema, item: ArtifactDataKeySchema.optional() })
  .strict()
export type ArtifactDataKeyRef = z.infer<typeof ArtifactDataKeyRefSchema>

// =============================================================================
// Manifest (KTD5)
// =============================================================================

/**
 * One declaration, as extracted from the page:
 * - `checklist`: one checklist item (`data-sj-piece="checklist" data-sj-key` on the list, `data-sj-item` on each
 *   entry); a checklist declares each item.
 * - `text`: one text field (`data-sj-piece="text" data-sj-key`).
 * - `custom`: one key from the `application/sj-data` block, with its value type.
 * - `list`: a list piece only; its item keys come from the server.
 */
export const ArtifactDataManifestEntrySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('checklist'), piece: ArtifactDataKeySchema, item: ArtifactDataKeySchema }).strict(),
  z.object({ type: z.literal('text'), piece: ArtifactDataKeySchema }).strict(),
  z
    .object({ type: z.literal('custom'), piece: ArtifactDataKeySchema, valueType: ArtifactDataCustomValueTypeSchema })
    .strict(),
  z.object({ type: z.literal('list'), piece: ArtifactDataKeySchema }).strict()
])
export type ArtifactDataManifestEntry = z.infer<typeof ArtifactDataManifestEntrySchema>

/**
 * The keys a page declares, recorded on the artifact. Refuses more than 200 declarations, the same declaration
 * twice (naming the key), and one piece key declared as two piece types (a checklist and a list both named `tasks`).
 */
export const ArtifactDataManifestSchema = z
  .object({ entries: z.array(ArtifactDataManifestEntrySchema).max(ARTIFACT_DATA_MAX_KEYS) })
  .strict()
  .superRefine((manifest, ctx) => {
    const pieceTypes = new Map<string, ArtifactDataPieceType>()
    const declared = new Set<string>()
    manifest.entries.forEach((entry, index) => {
      const known = pieceTypes.get(entry.piece)
      if (known !== undefined && known !== entry.type) {
        ctx.addIssue({
          code: 'custom',
          path: ['entries', index, 'type'],
          message: `piece "${entry.piece}" is declared as both ${known} and ${entry.type}`
        })
        return
      }
      pieceTypes.set(entry.piece, entry.type)
      // Keys may contain dots, so identity is the (piece, item) tuple, never a joined string.
      const item = entry.type === 'checklist' ? entry.item : null
      const identity = JSON.stringify([entry.piece, item])
      if (declared.has(identity)) {
        const name = item === null ? `"${entry.piece}"` : `piece "${entry.piece}" item "${item}"`
        ctx.addIssue({ code: 'custom', path: ['entries', index], message: `duplicate key ${name}` })
        return
      }
      declared.add(identity)
    })
  })
export type ArtifactDataManifest = z.infer<typeof ArtifactDataManifestSchema>

// =============================================================================
// Items (KTD4, R9)
// =============================================================================

const Piece = { piece: ArtifactDataKeySchema }
const Times = { createdAt: EpochMsSchema, updatedAt: EpochMsSchema }
const TeamNames = { createdBy: ActorNameSchema, updatedBy: ActorNameSchema }

/** The typed values, shown only when the stored value matches the declared type. */
const ChecklistValue = { type: z.literal('checklist'), item: ArtifactDataKeySchema, value: z.boolean().nullable() }
const TextValue = { type: z.literal('text'), value: TextSchema.nullable() }
const ListValue = { type: z.literal('list'), item: ArtifactDataKeySchema, value: ListTextSchema, order: OrderKeySchema }
const CustomValue = { type: z.literal('custom'), value: ArtifactDataScalarSchema }

/** A shown item as a team member sees it: who created and last changed it, by name. */
export const ArtifactDataItemSchema = z.discriminatedUnion('type', [
  z.object({ ...Piece, ...ChecklistValue, ...Times, ...TeamNames }).strict(),
  z.object({ ...Piece, ...TextValue, ...Times, ...TeamNames }).strict(),
  z.object({ ...Piece, ...ListValue, ...Times, ...TeamNames }).strict(),
  z.object({ ...Piece, ...CustomValue, ...Times, ...TeamNames }).strict()
])
export type ArtifactDataItem = z.infer<typeof ArtifactDataItemSchema>

/** A shown item as a grantee or public viewer sees it: when, never who. Strict, so a name fails the parse. */
export const ArtifactDataOutsiderItemSchema = z.discriminatedUnion('type', [
  z.object({ ...Piece, ...ChecklistValue, ...Times }).strict(),
  z.object({ ...Piece, ...TextValue, ...Times }).strict(),
  z.object({ ...Piece, ...ListValue, ...Times }).strict(),
  z.object({ ...Piece, ...CustomValue, ...Times }).strict()
])
export type ArtifactDataOutsiderItem = z.infer<typeof ArtifactDataOutsiderItemSchema>

/**
 * An item as an agent reads it (R5). It includes hidden rows (undeclared, or a stored value whose type no longer
 * matches the declaration, R11), so the value is any scalar and `shown` says whether viewers see it.
 */
export const ArtifactDataAgentItemSchema = z
  .object({
    ...Piece,
    item: ArtifactDataKeySchema.optional(),
    type: ArtifactDataPieceTypeSchema,
    value: ArtifactDataScalarSchema,
    order: OrderKeySchema.optional(),
    shown: z.boolean(),
    ...Times,
    ...TeamNames
  })
  .strict()
export type ArtifactDataAgentItem = z.infer<typeof ArtifactDataAgentItemSchema>

// =============================================================================
// Write operations (shared with the bridge)
// =============================================================================

/** Neighbours for a list add or move. The server computes the order key; a caller never supplies one. */
const NeighbourShape = {
  after: ArtifactDataKeySchema.nullable().optional(),
  before: ArtifactDataKeySchema.nullable().optional()
}

/**
 * Set a checklist item (`item` names it), a text field or a custom key (no `item`). The store checks the key
 * against the manifest and the value against the declared type.
 */
export const ArtifactDataSetParamsSchema = z
  .object({ piece: ArtifactDataKeySchema, item: ArtifactDataKeySchema.optional(), value: ArtifactDataScalarSchema })
  .strict()
export type ArtifactDataSetParams = z.infer<typeof ArtifactDataSetParamsSchema>

/** Add a list item. `clientId` makes a retry return the item the first attempt created. */
export const ArtifactDataAddParamsSchema = z
  .object({ piece: ArtifactDataKeySchema, clientId: ArtifactDataKeySchema, value: ListTextSchema, ...NeighbourShape })
  .strict()
export type ArtifactDataAddParams = z.infer<typeof ArtifactDataAddParamsSchema>

/** Remove a list item. */
export const ArtifactDataRemoveParamsSchema = z
  .object({ piece: ArtifactDataKeySchema, item: ArtifactDataKeySchema })
  .strict()
export type ArtifactDataRemoveParams = z.infer<typeof ArtifactDataRemoveParamsSchema>

/** Move a list item between neighbours. */
export const ArtifactDataMoveParamsSchema = z
  .object({ piece: ArtifactDataKeySchema, item: ArtifactDataKeySchema, ...NeighbourShape })
  .strict()
export type ArtifactDataMoveParams = z.infer<typeof ArtifactDataMoveParamsSchema>

export const ArtifactDataOperationSchema = z.discriminatedUnion('op', [
  ArtifactDataSetParamsSchema.extend({ op: z.literal('set') }).strict(),
  ArtifactDataAddParamsSchema.extend({ op: z.literal('add') }).strict(),
  ArtifactDataRemoveParamsSchema.extend({ op: z.literal('remove') }).strict(),
  ArtifactDataMoveParamsSchema.extend({ op: z.literal('move') }).strict()
])
export type ArtifactDataOperation = z.infer<typeof ArtifactDataOperationSchema>

// =============================================================================
// Routes: data get and data set (R5, R21)
// =============================================================================

const ArtifactPublicIdSchema = z.string().min(1).max(64)
const VersionSchema = z.number().int().positive()

/** `POST /artifacts/data/get`: an artifact's current data, addressed by its link. */
export const ArtifactDataGetRequestSchema = z.object({ publicId: ArtifactPublicIdSchema }).strict()
export type ArtifactDataGetRequest = z.infer<typeof ArtifactDataGetRequestSchema>

/**
 * The data as structured values. `untrusted: true` marks every value as page-authored input an agent must not
 * follow as instructions (R12).
 */
export const ArtifactDataGetResponseSchema = z
  .object({
    untrusted: z.literal(true),
    version: VersionSchema,
    items: z.array(ArtifactDataAgentItemSchema).max(ARTIFACT_DATA_MAX_ITEMS)
  })
  .strict()
export type ArtifactDataGetResponse = z.infer<typeof ArtifactDataGetResponseSchema>

/** `POST /artifacts/data/set`: a batch of writes, made as the team member whose session credential calls. */
export const ArtifactDataSetRequestSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    ops: z.array(ArtifactDataOperationSchema).min(1).max(ARTIFACT_DATA_SET_MAX_OPS)
  })
  .strict()
export type ArtifactDataSetRequest = z.infer<typeof ArtifactDataSetRequestSchema>

/** What one write touched; for an add, `item` is the key the server assigned. */
export const ArtifactDataWriteResultSchema = z
  .object({ piece: ArtifactDataKeySchema, item: ArtifactDataKeySchema.optional() })
  .strict()
export type ArtifactDataWriteResult = z.infer<typeof ArtifactDataWriteResultSchema>

/** One result per operation, in order. A refused batch answers `{ error }` instead (artifacts.ts). */
export const ArtifactDataSetResponseSchema = z
  .object({ results: z.array(ArtifactDataWriteResultSchema).max(ARTIFACT_DATA_SET_MAX_OPS) })
  .strict()
export type ArtifactDataSetResponse = z.infer<typeof ArtifactDataSetResponseSchema>
